import React, { useMemo } from 'react';
import { message } from 'antd';
import useQuery from 'hooks/useQuery';

import { Button, Typography, Link } from 'ui-kit';
import './SignUpPage.scss';
import { createOwner, newLogin, fetchMe } from '../../api/auth';
import { usePostHog } from '@posthog/react';
import { useHistory } from 'react-router-dom';
import useSocialAuth from 'hooks/useSocialAuth';
import SignUpLayout from 'components/SignUpLayout';
import SignUpForm from './SignUpForm';
import { FormProvider, useForm } from 'react-hook-form';
import { signupResolver } from './utils';

import { recordLeadForTolt } from 'utils/integrations';
import { useAnalytics } from 'analytics';
import { Role } from 'api/types/auth';
import _ from 'lodash';
import { URLS } from 'components/Routes/urls';
import { BASE_URL } from 'utils/sdk';

const SignUpPage = () => {
  const history = useHistory();
  const posthog = usePostHog();
  const capture = useAnalytics();
  const { showSocialAuthMessage } = useSocialAuth();

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      promoCode: '',
      firstName: '',
      email: '',
      password: '',
      phone: '',
      marketingCheckbox: false,
      captchaToken: '',
    },
    resolver: signupResolver,
  });

  const queryParams = useQuery();

  const queryParamsString = useMemo(
    () =>
      Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key]}`)
        .join('&'),
    [queryParams],
  );

  const create = (payload) => {
    createOwner(payload).then((result) => {
      if (result.ok) {
        window.fbq?.(
          'trackCustom',
          'CreateAccount',
          { from: 'email', email: result.data?.user?.email },
          { eventID: result?.data?.id },
        );
        const email = payload.email;
        const password = payload.password;
        newLogin(email, password).then(() => {
          // Must stay ahead of the redirect: the navigation below is synchronous, so anything
          // fired after it lands behind the next page's pageview.
          const signupProperties = {
            method: 'email',
            // Owners are HOST - the value `me.role` was already sending.
            role: Role.HOST,
            has_next_param: Boolean(_.get(queryParams, 'next', null)),
            marketing_consent: payload.marketingCheckbox,
          };
          capture('signup_completed', signupProperties);
          // Kept until nothing reads it.
          capture('user_signed_up', signupProperties);

          fetchMe()
            .then((me) => {
              posthog?.identify(String(me.user_id), {
                email: me.email,
                role: me.role,
              });
            })
            .catch(() => undefined);

          recordLeadForTolt({ email });
          if (Object.keys(queryParams).length > 0) {
            const hasNextParam = _.get(queryParams, 'next', null);
            if (hasNextParam) {
              let nextParam = queryParamsString.slice(queryParamsString.indexOf('next=') + 5);

              nextParam = nextParam.startsWith('/') ? nextParam.slice(1) : nextParam;

              window.location.href = `${BASE_URL}${nextParam}`;
            }
          } else {
            history.push(URLS.STORES);
          }
        });
      } else {
        if (Object.keys(result?.errors.fields).length > 0) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const { email, ...rest } = result?.errors?.fields;

          if (
            (email && email.includes('user with this email address already exists.')) ||
            !_.isUndefined(_.get(rest, '0'))
          ) {
            showSocialAuthMessage();
            return;
          }
        }

        if (result?.errors.nonFields.length > 0) {
          message.error(result?.errors.nonFields[0]);
        }
      }
    });
  };

  return (
    <SignUpLayout title="Create your Host Co account">
      <FormProvider {...methods}>
        <SignUpForm onSubmit={create} />
      </FormProvider>
      <Typography color="textGray" variant="caption">
        By continuing, you agree to The Host Co{' '}
        <Link className="Link-sign-up" href="https://www.thehost.co/terms-conditions">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link className="Link-sign-up" href="https://www.thehost.co/privacy-policy">
          Privacy Policy
        </Link>
        . We may send account-related messages to the number provided. Marketing texts are only sent
        with your consent. Msg & data rates may apply. Reply STOP to unsubscribe.
      </Typography>
      <Typography variant="body2" className="SignupPage-signin">
        Already have an account?{' '}
        <Button to={`/login/?${queryParamsString}`} className="SignupPage-singin-button">
          Log in
        </Button>
      </Typography>
    </SignUpLayout>
  );
};

export default SignUpPage;
